import React from 'react';

import { Box, Typography } from '@mui/material';

import { ActionLink, ListCollapsibleRow, ListRow, ListRowSkeleton } from 'app/components';

import type { EmployerListProps } from './EmployerList.types';

export const EmployerList = ({
  employers,
  selectionMode,
  showConnections,
  connectionFilter,
  isConnectionSelected,
  isEmployerSelected,
  onToggleConnection,
  onToggleEmployer,
  isLoading,
}: EmployerListProps) => {
  if (isLoading) {
    return <ListRowSkeleton rows={5} />;
  }

  const isSingleSelect = selectionMode === 'single';

  return (
    <Box
      sx={{
        '& > div:nth-of-type(odd)': { backgroundColor: 'common.white' },
        '& > div:nth-of-type(even)': { backgroundColor: '#F6FBFD' },
      }}
    >
      {employers.map(employer => {
        const enabledConnectionIds = connectionFilter
          ? employer.connections.filter(connectionFilter).map(c => c.connectionId)
          : employer.connections.map(c => c.connectionId);

        if (!showConnections) {
          return (
            <ListRow
              key={employer.employerId}
              onSelect={() => onToggleEmployer(employer.employerId, [])}
              selected={isEmployerSelected(employer.employerId)}
            >
              <Typography>
                <ActionLink to={`/employers/${employer.employerId}`}>{employer.employerName}</ActionLink>
              </Typography>
            </ListRow>
          );
        }

        const hasEnabledConnections = enabledConnectionIds.length > 0;
        const canToggleEmployer = !isSingleSelect && hasEnabledConnections;

        return (
          <ListCollapsibleRow
            key={employer.employerId}
            onSelect={canToggleEmployer ? () => onToggleEmployer(employer.employerId, enabledConnectionIds) : () => {}}
            selected={isEmployerSelected(employer.employerId)}
            disabled={!canToggleEmployer}
            rowHeader={
              <Typography sx={{ opacity: hasEnabledConnections ? 1 : 0.5 }}>
                <ActionLink to={`/employers/${employer.employerId}`}>{employer.employerName}</ActionLink>
              </Typography>
            }
          >
            {employer.connections.map(connection => {
              const isEnabled = connectionFilter ? connectionFilter(connection) : true;

              return (
                <ListRow
                  key={connection.connectionId}
                  onSelect={
                    isEnabled ? () => onToggleConnection(employer.employerId, connection.connectionId) : () => {}
                  }
                  selected={isEnabled && isConnectionSelected(employer.employerId, connection.connectionId)}
                  disabled={!isEnabled}
                  innerRow
                >
                  <Typography sx={{ opacity: isEnabled ? 1 : 0.5 }}>
                    <ActionLink to={`/employers/${employer.employerId}/connections/${connection.connectionId}`}>
                      {connection.name}
                    </ActionLink>
                  </Typography>
                </ListRow>
              );
            })}
          </ListCollapsibleRow>
        );
      })}
    </Box>
  );
};
